import { computed, toValue } from "vue"
import {
  CATEGORY,
  INVOICE_CATEGORIES,
  LINE_GRANULARITY,
} from "@/helpers/energyBillConstants.js"
import {
  AFRR_COLS,
  BELASTINGVERMINDERING_COLS,
  CONGESTIE_COLS,
  DAGMARKT_COLS,
  ENERGIEBELASTING_COLS,
  FCR_COLS,
  GEMISTE_PRODUCTIEVERGOEDING_COLS,
  INTRADAGMARKT_COLS,
  ONBALANS_COLS,
  PORTFOLIO_VOORDEEL_COLS,
  VARIABELE_VERGOEDING_COLS,
  VASTE_VERGOEDING_COLS,
  WINSTDELING_COLS,
  WINSTDELING_FCR_COLS,
  optimaFeeColumns,
  optimaColumns,
} from "@/helpers/energyBillColumns.js"

/**
 * Returns the value of the first available field in `fields` that has a
 * non-null value in the given `record`.
 *
 * @param {Object} record - The record to read from.
 * @param {string|string[]} fields - The field name or array of fallback field
 * names, tried in order.
 * @returns {*} The value of the first non-null field, or null if none found.
 */
function getFirstFieldValue(record, fields) {
  for (const field of [].concat(fields)) {
    const v = record?.[field]?.value
    if (v != null) {
      return v
    }
  }
  return null
}

/**
 * Returns whether the first available field in `fields` that has a non-null
 * value in the given `record` is marked as unstable.
 *
 * @param {Object} record - The record to read from.
 * @param {string|string[]} fields - The field name or array of fallback field
 * names, tried in order.
 * @returns {boolean} True if the first non-null field is marked as unstable,
 * false otherwise.
 */
function getFirstFieldUnstable(record, fields) {
  for (const field of [].concat(fields)) {
    const entry = record?.[field]
    if (entry?.value != null) {
      return entry.stable === false
    }
  }
  return false
}

function mapRows(records, columns) {
  return records.map((r) => {
    const row = { datum: r.datum }
    for (const col of columns) {
      if (col.srcField) {
        const raw = getFirstFieldValue(r, col.srcField)
        row[col.key] = col.scale && raw != null ? raw * col.scale : raw
      }
    }
    return row
  })
}

function deriveTariefRows(records, totalField, volumeField, rate) {
  return records.map((r) => ({
    datum: r.datum,
    volume: getFirstFieldValue(r, volumeField),
    vergoeding: rate,
    totaal: getFirstFieldValue(r, totalField),
  }))
}

function deriveFcrProfitShareRows(records) {
  return records.map((r) => {
    const fcrResultaat = getFirstFieldValue(r, "FCR_RESULT")
    const totaal = getFirstFieldValue(r, "FCR_PROFIT_SHARE")
    return {
      datum: r.datum,
      percentage: totaal != null && fcrResultaat ? totaal / fcrResultaat : null,
      fcrResultaat,
      totaal,
    }
  })
}

/**
 * Builds a single-row "Optima" service fee line (battery/solar/wind), whose
 * breakdown only shows the aggregate total and whose detail table has one
 * row for the current month with volume, tariff and total.
 */
function optimaFeeLine({
  key,
  label,
  subtitle,
  breakdownLabel,
  feeField,
  volumeField,
  priceField,
  val,
  isProvisional,
  hasRecord,
  monthLabel,
  volumeUnit,
}) {
  return {
    key,
    label,
    subtitle,
    category: CATEGORY.SERVICE_FEE,
    amount: val(feeField),
    isProvisional: isProvisional(feeField),
    noData: !hasRecord,
    granularity: LINE_GRANULARITY.MONTH,
    simpleBreakdown: {
      total: { label: breakdownLabel, amount: val(feeField) },
    },
    detailedColumns: optimaFeeColumns(volumeUnit),
    dailyRows: hasRecord
      ? [
          {
            datum: monthLabel,
            volume: val(volumeField),
            tarief: val(priceField),
            totaal: val(feeField),
          },
        ]
      : [],
  }
}

function placeholderLine(key, label, subtitle, columns, options = {}) {
  const granularity = options.granularity ?? LINE_GRANULARITY.MONTH
  const line = {
    key,
    label,
    subtitle,
    granularity,
    category: options.category,
    amount: null,
    isProvisional: false,
    noData: true,
    simpleBreakdown: null,
    detailedColumns: columns,
    dailyRows: [],
  }
  if (granularity !== LINE_GRANULARITY.MONTH) {
    line.quarterRows = []
  }
  return line
}

/**
 * Derives the shared values (record accessors, volumes and tariffs) that the
 * per-category line builders below need, so each builder only has to read from
 * a single context object.
 */
function buildLineContext({
  monthRecord,
  dailyRecords,
  quarterRecords,
  monthLabel,
}) {
  const record = toValue(monthRecord)
  const dRecords = toValue(dailyRecords)
  const qRecords = toValue(quarterRecords)
  const label = toValue(monthLabel)
  const hasRecord = !!record

  const val = (fields) => getFirstFieldValue(record, fields)
  const isProvisional = (fields) => getFirstFieldUnstable(record, fields)

  const takeOffVol = val("ALLOCATION_POINT_TAKE_OFF_VOLUME")
  const esf = val("ENERGY_SERVICE_FEE")
  const esfRate = esf != null && takeOffVol ? esf / takeOffVol : null
  const energyTax = val("ENERGY_TAX")
  const energyTaxRate =
    energyTax != null && takeOffVol ? energyTax / takeOffVol : null

  return {
    dRecords,
    qRecords,
    label,
    hasRecord,
    val,
    isProvisional,
    takeOffVol,
    esfRate,
    energyTaxRate,
  }
}

function buildMarketLines({
  val,
  isProvisional,
  hasRecord,
  dRecords,
  qRecords,
}) {
  // Portfolio-level records expose PORTFOLIO_IMBALANCE_* instead of ALLOCATION_POINT_IMBALANCE_*.
  const ONBALANS_TOTAAL = [
    "ALLOCATION_POINT_IMBALANCE_TOTAL_RESULT",
    "PORTFOLIO_IMBALANCE_TOTAL_RESULT",
  ]
  const ONBALANS_VERKOOP = [
    "ALLOCATION_POINT_IMBALANCE_FEED_IN_RESULT",
    "PORTFOLIO_IMBALANCE_FEED_IN_RESULT",
  ]
  const ONBALANS_INKOOP = [
    "ALLOCATION_POINT_IMBALANCE_TAKE_OFF_RESULT",
    "PORTFOLIO_IMBALANCE_TAKE_OFF_RESULT",
  ]
  const portfolioBenefit = val("PORTFOLIO_BENEFIT_REPOWERED")
  return [
    {
      key: "dagmarkt",
      label: "Resultaat | Dagmarkt",
      subtitle:
        "Het totaalresultaat van je handelsactiviteiten op de dagmarkt.",
      category: CATEGORY.MARKET,
      amount: val("DAY_AHEAD_TOTAL_RESULT"),
      isProvisional: isProvisional("DAY_AHEAD_TOTAL_RESULT"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        items: [
          {
            label: "Resultaat verkoop dagmarkt",
            amount: val("DAY_AHEAD_FEED_IN_RESULT"),
          },
          {
            label: "Resultaat inkoop dagmarkt",
            amount: val("DAY_AHEAD_TAKE_OFF_RESULT"),
          },
        ],
        total: {
          label: "Totaalresultaat dagmarkt",
          amount: val("DAY_AHEAD_TOTAL_RESULT"),
        },
      },
      detailedColumns: DAGMARKT_COLS,
      dailyRows: mapRows(dRecords, DAGMARKT_COLS),
      quarterRows: mapRows(qRecords, DAGMARKT_COLS),
    },
    {
      key: "intradagmarkt",
      label: "Resultaat | Intradaymarkt",
      subtitle:
        "Het totaalresultaat van je handelsactiviteiten op de intradaymarkt.",
      category: CATEGORY.MARKET,
      amount: val("INTRADAY_TOTAL_RESULT"),
      isProvisional: isProvisional("INTRADAY_TOTAL_RESULT"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        items: [
          {
            label: "Resultaat verkoop intradaymarkt",
            amount: val("INTRADAY_SELL_RESULT"),
          },
          {
            label: "Resultaat inkoop intradaymarkt",
            amount: val("INTRADAY_BUY_RESULT"),
          },
        ],
        total: {
          label: "Totaalresultaat intradaymarkt",
          amount: val("INTRADAY_TOTAL_RESULT"),
        },
      },
      detailedColumns: INTRADAGMARKT_COLS,
      dailyRows: mapRows(dRecords, INTRADAGMARKT_COLS),
      quarterRows: mapRows(qRecords, INTRADAGMARKT_COLS),
    },
    {
      key: "onbalans",
      label: "Resultaat | Onbalansmarkt",
      subtitle:
        "Het totaalresultaat van je handelsactiviteiten op de onbalansmarkt.",
      category: CATEGORY.MARKET,
      amount: val(ONBALANS_TOTAAL),
      isProvisional: isProvisional(ONBALANS_TOTAAL),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        items: [
          {
            label: "Resultaat verkoop onbalansmarkt",
            amount: val(ONBALANS_VERKOOP),
          },
          {
            label: "Resultaat inkoop onbalansmarkt",
            amount: val(ONBALANS_INKOOP),
          },
        ],
        total: {
          label: "Totaalresultaat onbalansmarkt",
          amount: val(ONBALANS_TOTAAL),
        },
      },
      detailedColumns: ONBALANS_COLS,
      dailyRows: mapRows(dRecords, ONBALANS_COLS),
      quarterRows: mapRows(qRecords, ONBALANS_COLS),
    },
    {
      key: "portfoliovoordeel",
      label: "Repowered Portfoliovoordeel",
      subtitle:
        "Het voordeel dat je ontvangt omdat je onderdeel bent van het Repowered klantportfolio.",
      category: CATEGORY.MARKET,
      amount: portfolioBenefit,
      isProvisional: isProvisional("PORTFOLIO_BENEFIT_REPOWERED"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        total: {
          label: "Repowered portfoliovoordeel",
          amount: portfolioBenefit,
        },
      },
      detailedColumns: PORTFOLIO_VOORDEEL_COLS,
      dailyRows: mapRows(dRecords, PORTFOLIO_VOORDEEL_COLS),
      quarterRows: mapRows(qRecords, PORTFOLIO_VOORDEEL_COLS),
    },
    {
      key: "fcr",
      label: "Resultaat | FCR",
      subtitle:
        "Het totaalresultaat van je bijdrage aan de Frequency Containment Reserve.",
      category: CATEGORY.MARKET,
      amount: val("FCR_RESULT"),
      isProvisional: isProvisional("FCR_RESULT"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        total: {
          label: "FCR veilingresultaten",
          amount: val("FCR_RESULT"),
        },
      },
      detailedColumns: FCR_COLS,
      dailyRows: mapRows(dRecords, FCR_COLS),
      quarterRows: mapRows(qRecords, FCR_COLS),
    },
    {
      key: "afrr",
      label: "Resultaat | aFRR",
      subtitle:
        "Het totaalresultaat van je bijdrage aan de automatic Frequency Restoration Reserve.",
      category: CATEGORY.MARKET,
      amount: val("AFRR_RESULT"),
      isProvisional: isProvisional("AFRR_RESULT"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        items: [
          {
            label: "Resultaat activatie",
            amount: val("AFRR_ASSET_ACTIVATED_RESULT"),
          },
          {
            label: "Pool-inkomsten",
            amount: val("AFRR_SHARED_CONTRIBUTION"),
          },
        ],
        total: {
          label: "Totaalresultaat aFRR",
          amount: val("AFRR_RESULT"),
        },
      },
      detailedColumns: AFRR_COLS,
      dailyRows: mapRows(dRecords, AFRR_COLS),
      quarterRows: mapRows(qRecords, AFRR_COLS),
    },
    placeholderLine(
      "congestiediensten",
      "Resultaat | Congestiediensten",
      "Het totaalresultaat van je lopende congestiecontract(en).",
      CONGESTIE_COLS,
      { category: CATEGORY.MARKET, granularity: LINE_GRANULARITY.QUARTER },
    ),
  ]
}

function buildServiceFeeLines({
  val,
  isProvisional,
  hasRecord,
  dRecords,
  qRecords,
  label,
  takeOffVol,
  esfRate,
}) {
  const fcrResult = val("FCR_RESULT")
  const fcrProfitShare = val("FCR_PROFIT_SHARE")
  const fcrPercentageFee =
    fcrProfitShare != null && fcrResult ? fcrProfitShare / fcrResult : null

  return [
    {
      key: "vaste-vergoeding",
      label: "Vaste vergoeding",
      subtitle: "De vaste vergoeding die je betaalt voor onze dienstverlening.",
      category: CATEGORY.SERVICE_FEE,
      amount: val("FIXED_FEE_RESULT"),
      isProvisional: isProvisional("FIXED_FEE_RESULT"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.MONTH,
      simpleBreakdown: {
        items: [
          {
            label: "Vaste vergoeding energielevering",
            amount: val("FIXED_FEE_RESULT"),
          },
          { label: "Vaste vergoeding congestiediensten", amount: null },
          { label: "Vaste vergoeding balanceringsdiensten", amount: null },
        ],
        total: {
          label: "Vaste vergoeding",
          amount: val("FIXED_FEE_RESULT"),
        },
      },
      detailedColumns: VASTE_VERGOEDING_COLS,
      dailyRows: hasRecord
        ? [
            {
              datum: label,
              vergoedingEnergielevering: val("FIXED_FEE_RESULT"),
              totaal: val("FIXED_FEE_RESULT"),
            },
          ]
        : [],
    },
    {
      key: "variabele-vergoeding",
      label: "Variabele vergoeding elektriciteit",
      subtitle:
        "De variabele vergoeding die je betaalt voor onze dienstverlening.",
      category: CATEGORY.SERVICE_FEE,
      amount: val("ENERGY_SERVICE_FEE"),
      isProvisional: isProvisional("ENERGY_SERVICE_FEE"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        items: [
          {
            label: "Absoluut gemeten volume",
            amount: takeOffVol,
            isVolume: true,
          },
          {
            label: "Variabele vergoeding (€/kWh)",
            amount: esfRate,
            isRate4: true,
            operator: "×",
          },
        ],
        total: {
          label: "Variabele vergoeding elektriciteit",
          amount: val("ENERGY_SERVICE_FEE"),
        },
      },
      detailedColumns: VARIABELE_VERGOEDING_COLS,
      dailyRows: deriveTariefRows(
        dRecords,
        "ENERGY_SERVICE_FEE",
        "ALLOCATION_POINT_TAKE_OFF_VOLUME",
        esfRate,
      ),
      quarterRows: deriveTariefRows(
        qRecords,
        "ENERGY_SERVICE_FEE",
        "ALLOCATION_POINT_TAKE_OFF_VOLUME",
        esfRate,
      ),
    },
    placeholderLine(
      "winstdeling-batterij",
      "Winstdeling | Optimalisatie batterijen",
      "De winstdeling over de optimalisatie van je batterij(en).",
      WINSTDELING_COLS,
      { category: CATEGORY.SERVICE_FEE },
    ),
    {
      key: "winstdeling-fcr",
      label: "Winstdeling | FCR",
      subtitle: "De winstdeling over de FCR-dienstverlening.",
      category: CATEGORY.SERVICE_FEE,
      amount: val("FCR_PROFIT_SHARE"),
      isProvisional: isProvisional("FCR_PROFIT_SHARE"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        items: [
          {
            label: "FCR Resultaat",
            amount: val("FCR_RESULT"),
          },
          {
            label: "Percentage (%)",
            amount: fcrPercentageFee,
            isPercentage: true,
            operator: "×",
          },
        ],
        total: {
          label: "Winstdeling FCR",
          amount: val("FCR_PROFIT_SHARE"),
        },
      },
      detailedColumns: WINSTDELING_FCR_COLS,
      dailyRows: deriveFcrProfitShareRows(dRecords),
      quarterRows: deriveFcrProfitShareRows(qRecords),
    },
    {
      key: "winstdeling-zon",
      label: "Winstdeling | Optimalisatie zon",
      subtitle:
        "De winstdeling over de optimalisatie van je zonne-installatie(s).",
      category: CATEGORY.SERVICE_FEE,
      amount: val("SMART_ENERGY_SOLAR_FEE"),
      isProvisional: isProvisional("SMART_ENERGY_SOLAR_FEE"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.MONTH,
      simpleBreakdown: {
        total: {
          label: "Winstdeling optimalisatie zon",
          amount: val("SMART_ENERGY_SOLAR_FEE"),
        },
      },
      detailedColumns: WINSTDELING_COLS,
      dailyRows: hasRecord
        ? [{ datum: label, totaal: val("SMART_ENERGY_SOLAR_FEE") }]
        : [],
    },
    placeholderLine(
      "winstdeling-wind",
      "Winstdeling | Optimalisatie wind",
      "De winstdeling over de optimalisatie van je windmolen(s).",
      WINSTDELING_COLS,
      { category: CATEGORY.SERVICE_FEE },
    ),
    placeholderLine(
      "winstdeling-eboiler",
      "Winstdeling | Optimalisatie e-boiler",
      "De winstdeling over de optimalisatie van je e-boiler(s).",
      WINSTDELING_COLS,
      { category: CATEGORY.SERVICE_FEE },
    ),
    optimaFeeLine({
      key: "optima-batterij",
      label: "Optima | Vergoeding batterij",
      subtitle: "De vergoeding over het vermogen van je batterij(en).",
      breakdownLabel: "Vergoeding batterij",
      feeField: "OPTIMA_BATTERY_FEE",
      volumeField: "OPTIMA_BATTERY_VOLUME",
      priceField: "OPTIMA_BATTERY_PRICE",
      monthLabel: label,
      volumeUnit: "kWh",
      val,
      isProvisional,
      hasRecord,
    }),
    optimaFeeLine({
      key: "optima-zon",
      label: "Optima | Vergoeding zon",
      subtitle: "De vergoeding over het vermogen van je zonne-installatie(s).",
      breakdownLabel: "Vergoeding zon",
      feeField: "OPTIMA_SOLAR_FEE",
      volumeField: "OPTIMA_SOLAR_VOLUME",
      priceField: "OPTIMA_SOLAR_PRICE",
      monthLabel: label,
      volumeUnit: "kW",
      val,
      isProvisional,
      hasRecord,
    }),
    optimaFeeLine({
      key: "optima-wind",
      label: "Optima | Vergoeding wind",
      subtitle: "De vergoeding over het vermogen van je windmolen(s).",
      breakdownLabel: "Vergoeding wind",
      feeField: "OPTIMA_WIND_FEE",
      volumeField: "OPTIMA_WIND_VOLUME",
      priceField: "OPTIMA_WIND_PRICE",
      monthLabel: label,
      volumeUnit: "kW",
      val,
      isProvisional,
      hasRecord,
    }),
    placeholderLine(
      "optima-laadinfra",
      "Optima | Vergoeding laadinfra",
      "De vergoeding over het vermogen van je laadinfra.",
      optimaColumns("Vermogen laadinfra (MW)"),
      { category: CATEGORY.SERVICE_FEE },
    ),
    placeholderLine(
      "optima-eboiler",
      "Optima | Vergoeding e-boiler",
      "De vergoeding over het vermogen van je e-boiler(s).",
      optimaColumns("Vermogen e-boiler (MW)"),
      { category: CATEGORY.SERVICE_FEE },
    ),
    {
      key: "gemiste-productievergoeding",
      label: "Gemiste productievergoeding",
      subtitle:
        "Vergoeding voor gemiste productie door tijdelijke beperkingen.",
      category: CATEGORY.SERVICE_FEE,
      amount: val("CBC_MISSED_PRODUCTION_RESULT"),
      isProvisional: isProvisional("CBC_MISSED_PRODUCTION_RESULT"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        total: {
          label: "Gemiste productievergoeding",
          amount: val("CBC_MISSED_PRODUCTION_RESULT"),
        },
      },
      detailedColumns: GEMISTE_PRODUCTIEVERGOEDING_COLS,
      dailyRows: mapRows(dRecords, GEMISTE_PRODUCTIEVERGOEDING_COLS),
      quarterRows: mapRows(qRecords, GEMISTE_PRODUCTIEVERGOEDING_COLS),
    },
  ]
}

function buildTaxLines({
  val,
  isProvisional,
  hasRecord,
  dRecords,
  qRecords,
  label,
  takeOffVol,
  energyTaxRate,
}) {
  return [
    {
      key: "energiebelasting",
      label: "Energiebelasting (voorschot)",
      subtitle:
        "De geschatte kosten voor energiebelasting die we namens jou inhouden.",
      category: CATEGORY.TAX,
      amount: val("ENERGY_TAX"),
      isProvisional: isProvisional("ENERGY_TAX"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.QUARTER,
      simpleBreakdown: {
        items: [
          {
            label: "Volume levering elektriciteit",
            amount: takeOffVol,
            isVolume: true,
          },
          {
            label: "Effectief tarief (€/kWh)",
            amount: energyTaxRate,
            isRate4: true,
            operator: "×",
          },
        ],
        total: {
          label: "Voorschot energiebelasting",
          amount: val("ENERGY_TAX"),
        },
      },
      detailedColumns: ENERGIEBELASTING_COLS,
      dailyRows: mapRows(dRecords, ENERGIEBELASTING_COLS),
      quarterRows: mapRows(qRecords, ENERGIEBELASTING_COLS),
    },
    {
      key: "belastingvermindering",
      label: "Belastingvermindering verblijfsfunctie",
      subtitle:
        "De heffingskorting die in mindering wordt gebracht op je energiebelasting.",
      category: CATEGORY.TAX,
      amount: val("ENERGY_TAX_REBATE"),
      isProvisional: isProvisional("ENERGY_TAX_REBATE"),
      noData: !hasRecord,
      granularity: LINE_GRANULARITY.MONTH,
      simpleBreakdown: {
        total: {
          label: "Belastingvermindering verblijfsfunctie",
          amount: val("ENERGY_TAX_REBATE"),
        },
      },
      detailedColumns: BELASTINGVERMINDERING_COLS,
      dailyRows: hasRecord
        ? [{ datum: label, totaal: val("ENERGY_TAX_REBATE") }]
        : [],
    },
  ]
}

/**
 * Builds the configured list of invoice lines with their breakdowns and detail
 * rows derived from monthly / daily / quarter records.
 */
export default function useInvoiceLines({
  monthRecord,
  dailyRecords,
  quarterRecords,
  monthLabel,
}) {
  const invoiceLines = computed(() => {
    const context = buildLineContext({
      monthRecord,
      dailyRecords,
      quarterRecords,
      monthLabel,
    })
    return [
      // ============== Marktresultaten ==============
      ...buildMarketLines(context),
      // ============== Vergoeding diensten ==============
      ...buildServiceFeeLines(context),
      // ============== Belasting en overheidsheffingen ==============
      ...buildTaxLines(context),
    ]
  })

  const groupedInvoiceLines = computed(() =>
    INVOICE_CATEGORIES.map((cat) => ({
      ...cat,
      lines: invoiceLines.value.filter((l) => l.category === cat.key),
    })).filter((g) => g.lines.length > 0),
  )

  return {
    invoiceLines,
    groupedInvoiceLines,
  }
}
